import { useState, useEffect } from "react";
import { AlertTriangle, BarChart2 } from "lucide-react";
import { ASSET_TYPES, ASSET_DEFAULTS } from '../constants';
import { computeFrontier } from '../utils/helpers';
import PageHeader from '../components/PageHeader';
import FrontierChart from '../components/FrontierChart';

export default function PortfolioPage() {
  const STORAGE_KEY = "hma_portfolio_v2";
  const [holdings, setHoldings] = useState(()=>{ try{return JSON.parse(localStorage.getItem(STORAGE_KEY)||"[]");}catch{return [];} });
  const [tab,      setTab]      = useState("stock");
  const [input,    setInput]    = useState({ ticker:"", amount:"", avgCost:"", label:"", value:"", expRet:"", expVol:"" });
  const [results,  setResults]  = useState(null);
  const [frontier, setFrontier] = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [narLoading,setNarLoading]=useState(false);
  const [narrative,setNarrative]=useState("");
  const [err,      setErr]      = useState("");

  /* ── Mobile detection ── */
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth <= 768); // eslint-disable-line no-unused-vars
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const save = h => { setHoldings(h); try{localStorage.setItem(STORAGE_KEY,JSON.stringify(h));}catch{} };

  const addHolding = () => {
    const id = Date.now();
    if (tab==="stock"||tab==="crypto") {
      const ticker = input.ticker.toUpperCase().trim().replace(tab==="crypto"?/[^A-Z]/g:/[^A-Z0-9]/g,"");
      const amount = parseFloat(input.amount);
      if (!ticker||!amount||amount<=0) return;
      const avgCost = parseFloat(input.avgCost)||null;
      save([...holdings.filter(h=>!(h.type===tab&&h.ticker===ticker)), { id,type:tab,ticker,shares:amount,avgCost }]);
    } else {
      const value = parseFloat(input.value);
      if (!value||value<=0) return;
      const label  = input.label||ASSET_TYPES.find(a=>a.key===tab)?.label||tab;
      const ticker = tab==="commodity"&&input.ticker ? input.ticker.toUpperCase().trim().replace(/[^A-Z0-9]/g,"") : null;
      const expRet = input.expRet ? parseFloat(input.expRet)/100 : null;
      const expVol = input.expVol ? parseFloat(input.expVol)/100 : null;
      const avgCost = parseFloat(input.avgCost)||null;
      save([...holdings, { id,type:tab,label,ticker,value,avgCost,expRet,expVol }]);
    }
    setInput({ ticker:"", amount:"", avgCost:"", label:"", value:"", expRet:"", expVol:"" });
  };

  const removeHolding = id => save(holdings.filter(h=>h.id!==id));

  const analyze = async () => {
    if (!holdings.length) return;
    setLoading(true); setResults(null); setFrontier(null); setNarrative(""); setErr("");

    const stocks    = holdings.filter(h=>h.type==="stock");
    const cryptos   = holdings.filter(h=>h.type==="crypto");
    const manuals   = holdings.filter(h=>!["stock","crypto"].includes(h.type));

    let stockPrices=[], histData=[];
    try {
      // Current prices + targets for stocks
      if (stocks.length) {
        const syms = stocks.map(h=>h.ticker).join(",");
        const r = await fetch(`/api/fmp?action=portfolio&symbols=${syms}`);
        if (!r.ok) throw new Error(`Price fetch failed (${r.status})`);
        stockPrices = await r.json();
        if (!Array.isArray(stockPrices)) throw new Error("Bad price data");
      }

      // 12-month history for stocks + crypto (efficient frontier)
      const hSyms = stocks.map(h=>h.ticker).join(",");
      const hCryp = cryptos.map(h=>h.ticker).join(",");
      if (hSyms||hCryp) {
        const p = new URLSearchParams({ action:"portfolio-history" });
        if (hSyms) p.set("symbols",hSyms);
        if (hCryp) p.set("cryptos",hCryp);
        const r2 = await fetch(`/api/fmp?${p}`);
        const raw = await r2.json();
        histData = Array.isArray(raw) ? raw : [];
      }
    } catch(e) {
      console.error("[Portfolio]",e);
      setErr(e.message||"Failed to fetch portfolio data.");
      setLoading(false); return;
    }

    // Build unified asset list
    const assets = [];
    const sectorColors = ["#60A5FA","#e8c875","#34D399","#F87171","#A78BFA","#FBBF24","#FB923C","#38BDF8"];

    stocks.forEach(h=>{
      const sd = stockPrices.find(x=>x.symbol===h.ticker)||{};
      const hd = histData.find(x=>x.symbol===h.ticker)||{};
      const price = sd.price||hd.currentPrice||null;
      if (!price) return;
      const value = price*h.shares;
      assets.push({
        id:h.id, type:"stock", ticker:h.ticker, label:sd.name||h.ticker,
        price, value, shares:h.shares,
        cost:h.avgCost?h.avgCost*h.shares:null,
        gainLoss:h.avgCost?( price-h.avgCost)*h.shares:null,
        gainPct:h.avgCost?(price-h.avgCost)/h.avgCost*100:null,
        sector:sd.sector||"Equities",
        targetMean:sd.targetMean||null,
        upside:sd.targetMean&&price?(sd.targetMean-price)/price*100:null,
        annualReturn:hd.annualReturn??null,
        annualVol:hd.annualVol??null,
        monthlyReturns:hd.monthlyReturns||[],
      });
    });

    cryptos.forEach(h=>{
      const hd = histData.find(x=>x.symbol===h.ticker)||{};
      const price = hd.currentPrice||null;
      if (!price) return;
      const value = price*h.shares;
      assets.push({
        id:h.id, type:"crypto", ticker:h.ticker, label:h.ticker,
        price, value, shares:h.shares,
        cost:h.avgCost?h.avgCost*h.shares:null,
        gainLoss:h.avgCost?(price-h.avgCost)*h.shares:null,
        gainPct:h.avgCost?(price-h.avgCost)/h.avgCost*100:null,
        sector:"Crypto",
        targetMean:null, upside:null,
        annualReturn:hd.annualReturn??null,
        annualVol:hd.annualVol??null,
        monthlyReturns:hd.monthlyReturns||[],
      });
    });

    manuals.forEach(h=>{
      const def = ASSET_DEFAULTS[h.type]||{ annualReturn:0.07, annualVol:0.15 };
      const secMap = { bond:"Fixed Income", cash:"Cash", commodity:"Commodities", private:"Private / Real Estate" };
      assets.push({
        id:h.id, type:h.type, ticker:h.ticker||null, label:h.label,
        price:null, value:h.value, shares:null,
        cost:h.avgCost||null, gainLoss:null, gainPct:null,
        sector:secMap[h.type]||"Other",
        targetMean:null, upside:null,
        annualReturn:h.expRet??def.annualReturn,
        annualVol:h.expVol??def.annualVol,
        monthlyReturns:[],
      });
    });

    if (!assets.length) { setErr("No valid positions found — check your tickers."); setLoading(false); return; }

    const totalValue = assets.reduce((s,a)=>s+a.value,0);
    assets.forEach(a=>{ a.pct=a.value/totalValue*100; a.weight=a.value/totalValue; });

    const sectors={};
    assets.forEach(a=>{ const s=a.sector||"Other"; sectors[s]=(sectors[s]||0)+a.pct; });

    // Grade from analyst upside on stocks only
    const stocksWithUpside = assets.filter(a=>a.upside!=null);
    const wUpside = stocksWithUpside.length
      ? stocksWithUpside.reduce((s,a)=>s+a.upside*a.weight,0)/stocksWithUpside.reduce((s,a)=>s+a.weight,0)
      : null;
    const grade = wUpside==null?null:wUpside>20?"A":wUpside>10?"B":wUpside>0?"C":wUpside>-10?"D":"F";
    const gradeColor = grade?(grade==="A"?"#00C805":grade==="B"?"#7CFC00":grade==="C"?"#e8c875":grade==="D"?"#FF9500":"#FF3B30"):"#888";

    const warnings=[];
    assets.forEach(a=>{ if(a.pct>30) warnings.push(`${a.label} is ${a.pct.toFixed(0)}% of your portfolio — heavily concentrated`); });
    Object.entries(sectors).forEach(([s,p])=>{ if(p>50) warnings.push(`${s} sector is ${p.toFixed(0)}% of your portfolio`); });
    if(assets.length<4) warnings.push(`Only ${assets.length} position${assets.length===1?"":"s"} — consider diversifying`);

    const totalCost = assets.reduce((s,a)=>s+(a.cost||0),0);
    const totalGL   = assets.reduce((s,a)=>s+(a.gainLoss||0),0);

    setResults({
      assets, totalValue, sectorColors,
      totalCost:totalCost>0?totalCost:null,
      totalGL:totalCost>0?totalGL:null,
      totalGLPct:totalCost>0?totalGL/totalCost*100:null,
      wUpside, grade, gradeColor, sectors, warnings,
    });

    // Efficient frontier (client-side math — runs in <5ms)
    if (assets.length>=2) {
      try { setFrontier(computeFrontier(assets)); } catch(e) { console.warn("Frontier error",e); }
    }

    setLoading(false);

    // Claude narrative
    setNarLoading(true);
    try {
      const assetSummary = assets.map(a=>`${a.label}(${a.type}): ${a.pct.toFixed(0)}% weight, annRet ${a.annualReturn!=null?(a.annualReturn*100).toFixed(1)+"%":"est"}, vol ${a.annualVol!=null?(a.annualVol*100).toFixed(1)+"%":"est"}${a.upside!=null?", analyst upside "+a.upside.toFixed(1)+"%":""}`).join("\n");
      const frontierNote = frontier ? `Portfolio Sharpe: ${frontier.current.sharpe.toFixed(2)}. Max-Sharpe Sharpe: ${frontier.maxSharpe.sharpe.toFixed(2)} at ${(frontier.maxSharpe.vol*100).toFixed(1)}% vol / ${(frontier.maxSharpe.ret*100).toFixed(1)}% return.` : "";
      const prompt = `Multi-asset portfolio risk analysis. Total value: $${totalValue.toFixed(0)}.\n\nHoldings:\n${assetSummary}\n\n${frontierNote}\n\nProvide a 3-paragraph risk-adjusted assessment: (1) overall risk/return profile and Sharpe quality, (2) diversification gaps or overexposures across asset classes, (3) one specific actionable rebalancing suggestion. Use the actual asset names. Be direct and quantitative.`;
      const r = await fetch("/api/claude",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({messages:[{role:"user",content:prompt}],max_tokens:450,system:"You are a quantitative portfolio analyst specializing in risk-adjusted returns and efficient frontier analysis. Be direct, use numbers, no disclaimers.",model:"claude-haiku-4-5-20251001"})});
      const d = await r.json();
      setNarrative(d.content?.[0]?.text||"");
    } catch { setNarrative("AI assessment unavailable. Portfolio data shown above."); }
    setNarLoading(false);
  };

  const fmtV = v=>v>=1e6?`$${(v/1e6).toFixed(2)}M`:v>=1e3?`$${(v/1e3).toFixed(1)}K`:`$${v.toFixed(2)}`;
  const tabClr = t => ASSET_TYPES.find(a=>a.key===t)?.color||"#e8c875";


  return (
    <div style={{ fontFamily:"'Inter',sans-serif", background:"#000", minHeight:"100vh", color:"#fff" }}>
      <style>{`
        html,body{background:#000!important}*{box-sizing:border-box}
        .pi{background:#111;border:1px solid rgba(255,255,255,0.12);border-radius:10px;color:#fff;padding:9px 12px;font-size:13px;outline:none;width:100%}
        .pi::placeholder{color:rgba(255,255,255,0.2)}
        .pi:focus{border-color:rgba(201,168,76,0.4)}
        .ptab{background:none;border:1px solid rgba(255,255,255,0.1);border-radius:8px;color:rgba(255,255,255,0.5);padding:6px 10px;font-size:11px;font-weight:700;cursor:pointer;transition:.15s}
        .ptab:hover{border-color:rgba(255,255,255,0.25);color:rgba(255,255,255,0.8)}
        .ptab.active{color:#000}

        /* ── Mobile overrides ── */
        @media (max-width: 768px) {
          .pi { min-height: 44px; font-size: 16px !important; padding: 10px 14px !important; }

          /* Stock/crypto input grid: single column */
          .pf-stock-grid {
            grid-template-columns: 1fr !important;
          }
          /* Manual asset input grid: single column */
          .pf-manual-grid {
            grid-template-columns: 1fr !important;
          }
          /* Add button: full width */
          .pf-add-btn {
            width: 100% !important;
            min-height: 48px !important;
            margin-top: 4px !important;
            white-space: normal !important;
          }

          /* Holdings list header: hide on mobile */
          .pf-holdings-header { display: none !important; }

          /* Holdings list rows: two-line stacked layout */
          .pf-holding-row {
            display: flex !important;
            flex-direction: column !important;
            gap: 2px !important;
            min-height: 56px !important;
            padding: 10px 14px !important;
          }
          .pf-holding-row-line1 {
            display: flex !important;
            align-items: center !important;
            gap: 8px !important;
          }
          .pf-holding-row-line2 {
            display: flex !important;
            align-items: center !important;
            gap: 8px !important;
            margin-top: 1px !important;
          }
          /* Hide the grid-based desktop cells on mobile */
          .pf-holding-desktop-cells { display: none !important; }
          /* Show the mobile stacked cells */
          .pf-holding-mobile-cells { display: flex !important; }

          /* Results holdings table header: hide on mobile */
          .pf-results-header { display: none !important; }
          /* Results holdings rows: stacked on mobile */
          .pf-results-row {
            display: flex !important;
            flex-direction: column !important;
            gap: 3px !important;
            min-height: 56px !important;
            padding: 10px 14px !important;
          }
          .pf-results-row-line1 {
            display: flex !important;
            align-items: center !important;
            gap: 8px !important;
          }
          .pf-results-row-line2 {
            display: flex !important;
            align-items: center !important;
            gap: 8px !important;
          }
          .pf-results-desktop-cells { display: none !important; }
          .pf-results-mobile-cells { display: flex !important; }
        }
      `}</style>
      <PageHeader active="portfolio" />
      <div style={{ maxWidth:960, margin:"0 auto", padding:"32px 24px 80px" }}>

        {/* Hero */}
        <div style={{ marginBottom:28 }}>
          <div className="hma-page-label">◆ Portfolio Analyzer</div>
          <div className="hma-page-title">Risk-adjusted portfolio analysis</div>
          <div style={{ fontSize:13, color:"rgba(255,255,255,0.45)", marginTop:4 }}>Stocks, crypto, bonds, cash, commodities, private holdings — see where you sit on the efficient frontier.</div>
        </div>

        {/* Input */}
        <div style={{ background:"#0F0F0F", border:"1px solid rgba(255,255,255,0.08)", borderRadius:16, padding:"18px", marginBottom:18 }}>
          <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:".1em", color:"rgba(255,255,255,0.35)", marginBottom:12 }}>Add Holding</div>

          {/* Type tabs */}
          <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:14 }}>
            {ASSET_TYPES.map(a=>(
              <button key={a.key} className={`ptab${tab===a.key?" active":""}`}
                style={tab===a.key?{background:a.color,borderColor:a.color}:{}}
                onClick={()=>{ setTab(a.key); setInput({ ticker:"", amount:"", avgCost:"", label:"", value:"", expRet:"", expVol:"" }); }}>
                {a.label}
              </button>
            ))}
          </div>

          {/* Stock / Crypto fields */}
          {(tab==="stock"||tab==="crypto") && (
            <div className="pf-stock-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr auto", gap:8 }}>
              <input className="pi" placeholder={tab==="stock"?"Ticker — AAPL":"Symbol — BTC"} value={input.ticker} maxLength={8}
                onChange={e=>setInput(v=>({...v,ticker:e.target.value}))} onKeyDown={e=>e.key==="Enter"&&addHolding()}/>
              <input className="pi" placeholder={tab==="stock"?"Shares — 10":"Amount — 0.5"} type="number" min="0" value={input.amount}
                onChange={e=>setInput(v=>({...v,amount:e.target.value}))} onKeyDown={e=>e.key==="Enter"&&addHolding()}/>
              <input className="pi" placeholder="Avg cost (optional)" type="number" min="0" value={input.avgCost}
                onChange={e=>setInput(v=>({...v,avgCost:e.target.value}))} onKeyDown={e=>e.key==="Enter"&&addHolding()}/>
              <button className="pf-add-btn" onClick={addHolding} disabled={!input.ticker||!input.amount}
                style={{ padding:"9px 18px", background:`linear-gradient(135deg,${tabClr(tab)}cc,${tabClr(tab)})`, color:"#000", border:"none", borderRadius:10, fontWeight:800, fontSize:14, cursor:"pointer", opacity:(!input.ticker||!input.amount)?0.4:1, whiteSpace:"nowrap" }}>
                Add +
              </button>
            </div>
          )}

          {/* Manual asset fields */}
          {!["stock","crypto"].includes(tab) && (
            <div className="pf-manual-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr auto", gap:8 }}>
              <input className="pi" placeholder={tab==="commodity"?"ETF Ticker (GLD)":"Label — e.g. T-Bills"} value={tab==="commodity"?input.ticker:input.label} maxLength={30}
                onChange={e=>setInput(v=>tab==="commodity"?{...v,ticker:e.target.value}:{...v,label:e.target.value})} onKeyDown={e=>e.key==="Enter"&&addHolding()}/>
              <input className="pi" placeholder="Value ($)" type="number" min="0" value={input.value}
                onChange={e=>setInput(v=>({...v,value:e.target.value}))} onKeyDown={e=>e.key==="Enter"&&addHolding()}/>
              <input className="pi" placeholder={`Est. return % (def ${((ASSET_DEFAULTS[tab]?.annualReturn||0.07)*100).toFixed(0)}%)`} type="number" min="-50" max="200" value={input.expRet}
                onChange={e=>setInput(v=>({...v,expRet:e.target.value}))} onKeyDown={e=>e.key==="Enter"&&addHolding()}/>
              <input className="pi" placeholder={`Est. vol % (def ${((ASSET_DEFAULTS[tab]?.annualVol||0.15)*100).toFixed(0)}%)`} type="number" min="0" max="200" value={input.expVol}
                onChange={e=>setInput(v=>({...v,expVol:e.target.value}))} onKeyDown={e=>e.key==="Enter"&&addHolding()}/>
              <button className="pf-add-btn" onClick={addHolding} disabled={!input.value}
                style={{ padding:"9px 18px", background:`linear-gradient(135deg,${tabClr(tab)}cc,${tabClr(tab)})`, color:"#000", border:"none", borderRadius:10, fontWeight:800, fontSize:14, cursor:"pointer", opacity:!input.value?0.4:1, whiteSpace:"nowrap" }}>
                Add +
              </button>
            </div>
          )}
        </div>

        {/* Holdings list */}
        {holdings.length > 0 && (
          <div style={{ background:"#0A0A0A", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, marginBottom:18, overflow:"hidden" }}>

            {/* Desktop header */}
            <div className="pf-holdings-header" style={{ padding:"8px 14px", display:"grid", gridTemplateColumns:"28px 1fr 80px 80px auto", gap:8, fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:".08em", color:"rgba(255,255,255,0.3)" }}>
              <span></span><span>Asset</span><span>Amount</span><span>Avg Cost</span><span></span>
            </div>

            {holdings.map(h=>{
              const tc = ASSET_TYPES.find(a=>a.key===h.type)?.color||"#e8c875";
              return (
                <div key={h.id} style={{ borderTop:"1px solid rgba(255,255,255,0.04)" }}>

                  {/* Desktop row */}
                  <div className="pf-holding-row" style={{ display:"grid", gridTemplateColumns:"28px 1fr 80px 80px auto", gap:8, padding:"9px 14px", alignItems:"center" }}>
                    <div style={{ width:8, height:8, borderRadius:"50%", background:tc, margin:"0 auto" }}/>

                    {/* Desktop-only cells (hidden on mobile via CSS) */}
                    <div className="pf-holding-desktop-cells" style={{ display:"contents" }}>
                      <div>
                        <span style={{ fontFamily:"'Rajdhani',sans-serif", fontWeight:900, color:tc, fontSize:14 }}>{h.ticker||h.label}</span>
                        {h.ticker&&h.label&&h.label!==h.ticker&&<span style={{ fontSize:11, color:"rgba(255,255,255,0.35)", marginLeft:6 }}>{h.label}</span>}
                        <span style={{ fontSize:10, color:"rgba(255,255,255,0.25)", marginLeft:6 }}>{h.type}</span>
                      </div>
                      <span style={{ fontSize:12, color:"rgba(255,255,255,0.7)" }}>{h.shares!=null?h.shares.toLocaleString():fmtV(h.value)}</span>
                      <span style={{ fontSize:12, color:"rgba(255,255,255,0.4)" }}>{h.avgCost?`$${h.avgCost}`:"—"}</span>
                      <button onClick={()=>removeHolding(h.id)} style={{ background:"rgba(255,59,48,0.07)", border:"1px solid rgba(255,59,48,0.18)", color:"#FF3B30", borderRadius:7, padding:"3px 9px", fontSize:11, cursor:"pointer", fontWeight:700 }}>✕</button>
                    </div>

                    {/* Mobile stacked cells (shown on mobile via CSS) */}
                    <div className="pf-holding-mobile-cells" style={{ display:"none", flexDirection:"column", gap:2, gridColumn:"2 / -1" }}>
                      {/* Line 1: ticker + shares */}
                      <div style={{ display:"flex", alignItems:"center", gap:8, justifyContent:"space-between" }}>
                        <div>
                          <span style={{ fontFamily:"'Rajdhani',sans-serif", fontWeight:900, color:tc, fontSize:15 }}>{h.ticker||h.label}</span>
                          <span style={{ fontSize:10, color:"rgba(255,255,255,0.25)", marginLeft:6, textTransform:"uppercase" }}>{h.type}</span>
                        </div>
                        <span style={{ fontSize:13, color:"rgba(255,255,255,0.7)", fontWeight:600 }}>
                          {h.shares!=null?`${h.shares.toLocaleString()} shares`:fmtV(h.value)}
                        </span>
                      </div>
                      {/* Line 2: avg cost + remove */}
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                        <span style={{ fontSize:11, color:"rgba(255,255,255,0.35)" }}>
                          {h.avgCost ? `Avg cost $${h.avgCost}` : "No avg cost"}
                        </span>
                        <button onClick={()=>removeHolding(h.id)} style={{ background:"rgba(255,59,48,0.07)", border:"1px solid rgba(255,59,48,0.18)", color:"#FF3B30", borderRadius:7, padding:"4px 12px", minHeight:36, fontSize:12, cursor:"pointer", fontWeight:700 }}>✕</button>
                      </div>
                    </div>
                  </div>

                </div>
              );
            })}

            <div style={{ padding:"12px 14px", borderTop:"1px solid rgba(255,255,255,0.06)" }}>
              <button onClick={analyze} disabled={loading||!holdings.length}
                style={{ width:"100%", padding:"13px", background:loading?"rgba(201,168,76,0.12)":"linear-gradient(135deg,#1B4F8A,#2563EB)", color:"#fff", border:"none", borderRadius:11, fontWeight:800, fontSize:15, cursor:loading?"default":"pointer", opacity:loading?0.7:1 }}>
                {loading?"Fetching data & computing frontier…":`Analyze ${holdings.length} Holding${holdings.length===1?"":"s"} →`}
              </button>
            </div>
          </div>
        )}

        {err && <div style={{ color:"#FF3B30", fontSize:13, marginBottom:16, padding:"10px 14px", background:"rgba(255,59,48,0.06)", borderRadius:8 }}>{err}</div>}

        {/* Results */}
        {results && (
          <div>
            {/* Summary cards */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))", gap:10, marginBottom:18 }}>
              {results.grade && (
                <div style={{ background:"#0F0F0F", border:`1px solid ${results.gradeColor}30`, borderRadius:14, padding:"16px", textAlign:"center" }}>
                  <div style={{ fontSize:48, fontWeight:900, color:results.gradeColor, lineHeight:1, fontFamily:"'Cinzel',serif" }}>{results.grade}</div>
                  <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:".12em", color:"rgba(255,255,255,0.3)", marginTop:3 }}>Analyst Grade</div>
                </div>
              )}
              <div style={{ background:"#0F0F0F", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:"16px" }}>
                <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:".12em", color:"rgba(255,255,255,0.35)", marginBottom:5 }}>Total Value</div>
                <div style={{ fontSize:20, fontWeight:900, color:"#fff" }}>{fmtV(results.totalValue)}</div>
                {results.totalCost&&<div style={{ fontSize:10, color:"rgba(255,255,255,0.35)", marginTop:2 }}>Cost {fmtV(results.totalCost)}</div>}
              </div>
              {results.totalGL!=null&&(
                <div style={{ background:"#0F0F0F", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:"16px" }}>
                  <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:".12em", color:"rgba(255,255,255,0.35)", marginBottom:5 }}>Total Gain / Loss</div>
                  <div style={{ fontSize:20, fontWeight:900, color:results.totalGL>=0?"#00C805":"#FF3B30" }}>{results.totalGL>=0?"+":""}{fmtV(Math.abs(results.totalGL))}</div>
                  <div style={{ fontSize:10, color:results.totalGL>=0?"#00C805":"#FF3B30" }}>{results.totalGLPct>=0?"+":""}{results.totalGLPct?.toFixed(1)}%</div>
                </div>
              )}
              {frontier&&(
                <div style={{ background:"#0F0F0F", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:"16px" }}>
                  <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:".12em", color:"rgba(255,255,255,0.35)", marginBottom:5 }}>Portfolio Sharpe</div>
                  <div style={{ fontSize:20, fontWeight:900, color:frontier.current.sharpe>=1?"#00C805":frontier.current.sharpe>=0.5?"#e8c875":"#FF9500" }}>{frontier.current.sharpe.toFixed(2)}</div>
                  <div style={{ fontSize:10, color:"rgba(255,255,255,0.35)" }}>Best available: {frontier.maxSharpe.sharpe.toFixed(2)}</div>
                </div>
              )}
              {frontier&&(
                <div style={{ background:"#0F0F0F", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:"16px" }}>
                  <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:".12em", color:"rgba(255,255,255,0.35)", marginBottom:5 }}>Portfolio Vol</div>
                  <div style={{ fontSize:20, fontWeight:900, color:"#fff" }}>{(frontier.current.vol*100).toFixed(1)}%</div>
                  <div style={{ fontSize:10, color:"rgba(255,255,255,0.35)" }}>Min available: {(frontier.minVol.vol*100).toFixed(1)}%</div>
                </div>
              )}
            </div>

            {/* Warnings */}
            {results.warnings.length>0&&(
              <div style={{ marginBottom:16 }}>
                {results.warnings.map((w,i)=>(
                  <div key={i} style={{ display:"flex", alignItems:"center", gap:10, background:"rgba(255,149,0,0.06)", border:"1px solid rgba(255,149,0,0.18)", borderRadius:9, padding:"9px 13px", marginBottom:6 }}>
                    <AlertTriangle size={13} strokeWidth={2} color="#FF9500"/>
                    <span style={{ fontSize:13, color:"rgba(255,255,255,0.8)" }}>{w}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Efficient Frontier chart */}
            {frontier&&(
              <div style={{ background:"#0C0C0C", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:"18px", marginBottom:16 }}>
                <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:".1em", color:"rgba(255,255,255,0.4)", marginBottom:4 }}>Efficient Frontier</div>
                <div style={{ fontSize:11, color:"rgba(255,255,255,0.3)", marginBottom:14 }}>
                  <span style={{ color:"#e8c875", fontWeight:700 }}>● You</span> &nbsp;·&nbsp;
                  <span style={{ color:"#00C805", fontWeight:700 }}>● Best Sharpe</span> &nbsp;·&nbsp;
                  <span style={{ color:"#60A5FA", fontWeight:700 }}>○ Min Risk</span> &nbsp;·&nbsp;
                  <span>500 random allocations of your holdings</span>
                </div>
                <FrontierChart data={frontier}/>
                {frontier.maxSharpe.sharpe > frontier.current.sharpe+0.1&&(
                  <div style={{ marginTop:12, padding:"10px 14px", background:"rgba(0,200,5,0.05)", border:"1px solid rgba(0,200,5,0.15)", borderRadius:9, fontSize:12, color:"rgba(255,255,255,0.7)" }}>
                    <strong style={{ color:"#00C805" }}>Optimization gap:</strong> The max-Sharpe allocation targets {(frontier.maxSharpe.ret*100).toFixed(1)}% return at {(frontier.maxSharpe.vol*100).toFixed(1)}% risk (Sharpe {frontier.maxSharpe.sharpe.toFixed(2)}) vs your current {(frontier.current.ret*100).toFixed(1)}% / {(frontier.current.vol*100).toFixed(1)}% (Sharpe {frontier.current.sharpe.toFixed(2)}).
                  </div>
                )}
              </div>
            )}

            {/* Sector allocation */}
            {Object.keys(results.sectors).length>1&&(
              <div style={{ background:"#0F0F0F", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:"16px 18px", marginBottom:14 }}>
                <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:".1em", color:"rgba(255,255,255,0.35)", marginBottom:10 }}>Asset Class / Sector Allocation</div>
                <div style={{ display:"flex", height:9, borderRadius:5, overflow:"hidden", marginBottom:10, gap:1 }}>
                  {Object.entries(results.sectors).sort((a,b)=>b[1]-a[1]).map(([s,p],i)=>(
                    <div key={s} style={{ width:`${p}%`, background:results.sectorColors[i%8] }}/>
                  ))}
                </div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:"5px 14px" }}>
                  {Object.entries(results.sectors).sort((a,b)=>b[1]-a[1]).map(([s,p],i)=>(
                    <div key={s} style={{ display:"flex", alignItems:"center", gap:5, fontSize:11 }}>
                      <div style={{ width:7, height:7, borderRadius:"50%", background:results.sectorColors[i%8] }}/>
                      <span style={{ color:"rgba(255,255,255,0.55)" }}>{s}</span>
                      <span style={{ fontWeight:700, color:"rgba(255,255,255,0.85)" }}>{p.toFixed(0)}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Holdings results table */}
            <div style={{ background:"#0A0A0A", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, overflow:"hidden", marginBottom:14 }}>

              {/* Desktop header */}
              <div className="pf-results-header" style={{ padding:"9px 14px", borderBottom:"1px solid rgba(255,255,255,0.05)", display:"grid", gridTemplateColumns:"100px 1fr 70px 60px 70px 70px 70px", gap:8, fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:".07em", color:"rgba(255,255,255,0.28)" }}>
                <span>Asset</span><span>Name</span><span>Value</span><span>Wt</span><span>1Y Ret</span><span>1Y Vol</span><span>G/L</span>
              </div>

              {results.assets.sort((a,b)=>b.value-a.value).map(a=>{
                const tc = ASSET_TYPES.find(x=>x.key===a.type)?.color||"#e8c875";
                const retClr = a.annualReturn==null?"rgba(255,255,255,0.35)":a.annualReturn>=0?"#00C805":"#FF3B30";
                const glClr  = a.gainLoss==null?"rgba(255,255,255,0.35)":a.gainLoss>=0?"#00C805":"#FF3B30";
                return (
                  <div key={a.id} style={{ borderBottom:"1px solid rgba(255,255,255,0.03)" }}>

                    {/* Desktop row */}
                    <div className="pf-results-row" style={{ display:"grid", gridTemplateColumns:"100px 1fr 70px 60px 70px 70px 70px", gap:8, padding:"10px 14px", alignItems:"center", fontSize:12 }}>

                      {/* Desktop-only grid cells */}
                      <div className="pf-results-desktop-cells" style={{ display:"contents" }}>
                        <div>
                          {a.type==="stock"
                            ? <a href={`/?t=${a.ticker}`} style={{ fontFamily:"'Rajdhani',sans-serif", fontWeight:900, color:tc, fontSize:14, textDecoration:"none" }}>{a.ticker}</a>
                            : <span style={{ fontFamily:"'Rajdhani',sans-serif", fontWeight:900, color:tc, fontSize:14 }}>{a.ticker||a.label.slice(0,8)}</span>
                          }
                          <span style={{ display:"block", fontSize:11, color:"rgba(255,255,255,0.25)", textTransform:"uppercase", marginTop:1 }}>{a.type}</span>
                        </div>
                        <div style={{ color:"rgba(255,255,255,0.7)", fontSize:12, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{a.label}</div>
                        <span style={{ color:"rgba(255,255,255,0.8)", fontWeight:600 }}>{fmtV(a.value)}</span>
                        <span style={{ fontWeight:700, color:"rgba(255,255,255,0.7)" }}>{a.pct.toFixed(1)}%</span>
                        <span style={{ fontWeight:700, color:retClr }}>{a.annualReturn!=null?(a.annualReturn>=0?"+":""+(a.annualReturn*100).toFixed(1)+"%"):"est"}</span>
                        <span style={{ fontWeight:700, color:"rgba(255,255,255,0.6)" }}>{a.annualVol!=null?(a.annualVol*100).toFixed(1)+"%":"est"}</span>
                        <span style={{ fontWeight:700, color:glClr }}>{a.gainLoss!=null?(a.gainLoss>=0?"+":"")+fmtV(Math.abs(a.gainLoss)):"—"}</span>
                      </div>

                      {/* Mobile stacked cells */}
                      <div className="pf-results-mobile-cells" style={{ display:"none", flexDirection:"column", gap:3, gridColumn:"1 / -1" }}>
                        {/* Line 1: ticker + value + weight */}
                        <div style={{ display:"flex", alignItems:"center", gap:8, justifyContent:"space-between" }}>
                          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                            {a.type==="stock"
                              ? <a href={`/?t=${a.ticker}`} style={{ fontFamily:"'Rajdhani',sans-serif", fontWeight:900, color:tc, fontSize:15, textDecoration:"none" }}>{a.ticker}</a>
                              : <span style={{ fontFamily:"'Rajdhani',sans-serif", fontWeight:900, color:tc, fontSize:15 }}>{a.ticker||a.label.slice(0,8)}</span>
                            }
                            <span style={{ fontSize:10, color:"rgba(255,255,255,0.25)", textTransform:"uppercase" }}>{a.type}</span>
                          </div>
                          <div style={{ textAlign:"right" }}>
                            <span style={{ fontWeight:700, color:"rgba(255,255,255,0.9)", fontSize:13 }}>{fmtV(a.value)}</span>
                            <span style={{ fontSize:11, color:"rgba(255,255,255,0.4)", marginLeft:5 }}>{a.pct.toFixed(1)}%</span>
                          </div>
                        </div>
                        {/* Line 2: gain/loss + 1Y return + vol */}
                        <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                          <span style={{ fontSize:11, color:"rgba(255,255,255,0.45)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:120 }}>{a.label}</span>
                          <span style={{ fontSize:11, fontWeight:700, color:retClr }}>
                            Ret: {a.annualReturn!=null?(a.annualReturn>=0?"+":""+(a.annualReturn*100).toFixed(1)+"%"):"est"}
                          </span>
                          <span style={{ fontSize:11, fontWeight:700, color:glClr }}>
                            {a.gainLoss!=null?"G/L: "+(a.gainLoss>=0?"+":"")+fmtV(Math.abs(a.gainLoss)):""}
                          </span>
                        </div>
                      </div>

                    </div>
                  </div>
                );
              })}
            </div>

            {/* AI Narrative */}
            {(narrative||narLoading)&&(
              <div style={{ background:"linear-gradient(145deg,#0D0900,#161000)", border:"1px solid rgba(201,168,76,0.18)", borderRadius:14, padding:"18px 20px" }}>
                <div style={{ fontSize:11, fontWeight:800, textTransform:"uppercase", letterSpacing:".15em", color:"#e8c875", marginBottom:10 }}>◆ Risk-Adjusted Insight</div>
                {narLoading
                  ? <div style={{ display:"flex", gap:6, alignItems:"center" }}>{[0,1,2].map(i=><div key={i} style={{ width:7,height:7,borderRadius:"50%",background:"#e8c875",animation:`hmapulse 1.2s ${i*0.22}s ease-in-out infinite` }}/>)}<span style={{ fontSize:12,color:"rgba(255,255,255,0.4)",marginLeft:6 }}>Generating risk analysis…</span></div>
                  : <div style={{ fontSize:14, color:"rgba(255,255,255,0.82)", lineHeight:1.75, whiteSpace:"pre-wrap" }}>{narrative}</div>
                }
              </div>
            )}
          </div>
        )}

        {holdings.length===0&&(
          <div style={{ textAlign:"center", padding:"60px 20px", color:"rgba(255,255,255,0.28)" }}>
            <div style={{ marginBottom:14, display:"flex", justifyContent:"center" }}><BarChart2 size={44} strokeWidth={1.5} color="rgba(201,168,76,0.5)"/></div>
            <div style={{ fontSize:17, fontWeight:700, marginBottom:5, color:"rgba(255,255,255,0.45)" }}>Build your portfolio</div>
            <div style={{ fontSize:13 }}>Add stocks, crypto, bonds, cash, or private holdings above.</div>
          </div>
        )}
      </div>
    </div>
  );
}
